import { useCallback, useMemo, useState } from "react";
import closeIcon from "src/assests/images/dashboard/Vector 3.png";
import PayBillAmountModal from "./PayBillsAmountModal";
import {
  itemTotalTaxCalc,
  itemsTotalPriceCalc,
} from "src/utils/order-price-calculation";
import { convertCentsToDollars, convertDollarToCents, formatDecimalInput } from "src/helpers";
import { useDiscount } from "../../hooks/useDiscount";
import { useCreateCashPayment } from "../../hooks/useCreateCashPayment";
import CustomerInfoSection from "./CustomerInfoSection";
import PaymentMethodSection from "./PaymentMethodSection";
import PromoCodeSection from "./PromoCodeSection";
import { PayBillModalProps } from "../../../../interfaces/order-management-props.interface";
import { PaymentMethodTypeEnum } from "src/enums/order.enum";
import { useAuth } from "src/contexts/auth-provider";
import { FEATURE_NAMES } from "src/utils/isAllowedFeature";
import { PortaledModalFrame } from "src/components/common/PortaledModalFrame";
import { failureToast } from "src/utils/toasts";

const PayBillModal = ({
  onClose,
  customerOrder,
  fromOrderCreate = false,
  getActiveCustomerOrders,
}: PayBillModalProps) => {
  const [showBillAmountModal, setShowBillAmountModal] = useState(false);
  const [cashInput, setCashInput] = useState("");
  const [payment, setPayment] = useState(PaymentMethodTypeEnum.CASH);
  const [showPromoField, setShowPromoField] = useState(false);
  const [cashCheckoutLoading, setCashCheckoutLoading] = useState(false);

  const { permissions, hasRestaurant } = useAuth();
  const showDiscounts = permissions[FEATURE_NAMES.DiscountsAtCheckout];
  const createCashPayment = useCreateCashPayment();

  const {
    promoCode,
    setPromoCode,
    discountAmountCents,
    validatePromotionCode,
    isDiscountLoading,
    validationMessage,
    isValidationError,
    appliedPromotionCode,
  } = useDiscount(customerOrder);

  const itemsTotalPrice = itemsTotalPriceCalc(customerOrder?.items);
  const itemTotalTax = itemTotalTaxCalc(customerOrder?.items);

  const finalTotalCents = useMemo(() => {
    const subtotalAndTax = Math.round(itemsTotalPrice + itemTotalTax);
    return Math.max(0, subtotalAndTax - (discountAmountCents || 0) + (customerOrder?.cardProcessingFeeCents || 0));
  }, [itemsTotalPrice, itemTotalTax, discountAmountCents, customerOrder?.cardProcessingFeeCents]);

  const totalAmount = useMemo(() => {
    return convertCentsToDollars(finalTotalCents);
  }, [finalTotalCents]);

  const tenderedCents = useMemo(() => convertDollarToCents(cashInput), [cashInput]);

  const isCashCheckoutEnabled = useMemo(() => {
    if (payment !== PaymentMethodTypeEnum.CASH) return false;
    if (finalTotalCents <= 0) return false;
    return tenderedCents >= finalTotalCents;
  }, [payment, finalTotalCents, tenderedCents]);

  const handleCashInputChange = (raw: string) => {
    setCashInput(formatDecimalInput(raw, 2));
  };

  const handleCashCheckoutClick = async () => {
    if (!customerOrder?.correlationId || !hasRestaurant?.correlationId) {
      failureToast("Missing order or store.");
      return;
    }
    const changeCents = tenderedCents - finalTotalCents;
    if (changeCents < 0) {
      failureToast("Amount received is less than the total.");
      return;
    }
    setCashCheckoutLoading(true);
    try {
      await createCashPayment({
        customerOrderCorrelationId: customerOrder.correlationId,
        paymentAmountCents: tenderedCents,
        changeCents,
      });
      setShowBillAmountModal(true);
    } catch (e: any) {
      const msg = e?.message || "Cash payment failed.";
      failureToast(msg);
    } finally {
      setCashCheckoutLoading(false);
    }
  };

  const handleCardPaymentSuccess = useCallback(() => {
    onClose();
    getActiveCustomerOrders?.();
  }, [onClose, getActiveCustomerOrders]);

  const handleTotalOff = async () => {
    validatePromotionCode();
  };

  const finalTotal = finalTotalCents;

  return (
    <>
      <PortaledModalFrame onClose={onClose} panelStyle={{ maxWidth: 820 }}>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-[#F1F1F2] px-5 py-3 sm:px-8">
            <h1 id="modal-title" className="text-lg font-semibold">
              Pay Bill
            </h1>
            <button
              type="button"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100"
              onClick={onClose}
              aria-label="Close"
            >
              <img className="h-3 w-3" src={closeIcon} alt="" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-5 pt-3 sm:px-8 sm:pb-6 sm:pt-3">
            <div className="flex min-w-0 flex-col gap-4 lg:flex-row lg:items-stretch lg:gap-x-6">
              <div className="min-w-0 w-full shrink-0 lg:w-[46%]">
                <CustomerInfoSection
                  customerOrder={customerOrder}
                  itemsTotalPrice={itemsTotalPrice}
                  itemTotalTax={itemTotalTax}
                  discountAmountCents={discountAmountCents}
                  promotionCode={appliedPromotionCode}
                  finalTotal={finalTotal}
                />

                {showDiscounts && (
                  <PromoCodeSection
                    showPromoField={showPromoField}
                    promoCode={promoCode}
                    promoloading={isDiscountLoading}
                    promoMessage={validationMessage}
                    promoError={isValidationError}
                    onShowPromoField={() => setShowPromoField(true)}
                    onHidePromoField={() => setShowPromoField(false)}
                    onPromoCodeChange={setPromoCode}
                    onApplyPromo={handleTotalOff}
                  />
                )}
              </div>

              <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                <PaymentMethodSection
                  payment={payment}
                  cashInput={cashInput}
                  isCashCheckoutEnabled={isCashCheckoutEnabled}
                  cashCheckoutLoading={cashCheckoutLoading}
                  storeCorrelationId={hasRestaurant?.correlationId}
                  customerOrderCorrelationId={customerOrder?.correlationId}
                  onPaymentChange={setPayment}
                  onCashInputChange={handleCashInputChange}
                  onCashCheckoutClick={handleCashCheckoutClick}
                  onCardPaymentSuccess={handleCardPaymentSuccess}
                />
              </div>
            </div>
          </div>
        </div>
      </PortaledModalFrame>
      {showBillAmountModal && (
        <PayBillAmountModal
          paidAmount={Number(cashInput) || 0}
          actualAmount={totalAmount}
          showBillAmountModal={() => setShowBillAmountModal(false)}
          onClose={onClose}
          getActiveCustomerOrders={getActiveCustomerOrders}
          fromOrderCreate={fromOrderCreate}
        />
      )}
    </>
  );
};

export default PayBillModal;
